import { Dialog, Heading, Pane, Paragraph, Spinner } from 'evergreen-ui';
import React, { MutableRefObject, useCallback } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useObservable } from 'rxsupport';
import { UsePopupTriggerHook } from '../../common/hook';
import { FullProviderUserInfo } from '../domain/types';
import { USER_POPUP_HEIGHT } from './const';
import { CancelButton, OkButton } from './ProviderUserForm/component/FormButtons';
import { EditUserFormContent } from './ProviderUserForm/component/FormContents';
import { isUserActive } from './ProviderUserForm/component/UserStatus';
import { useEditProviderUserMutation$ } from './ProviderUserForm/hook/useEditProviderUserMutation$';
import { useProviderUserByIdQuery } from './ProviderUserForm/hook/useProviderUserByIdQuery';
import { UserFormInput } from './ProviderUserForm/types';
import {showToast} from "common";
import { useApolloClient } from '@apollo/client';
import { useCurrentUser } from 'security';
import { ProviderPortalFeatureFlag, useFeatureFlag } from 'featureflag';


type EditUserPopupProps = {
    selectedUserIdRef: MutableRefObject<string | null>;
    onSuccess?: () => Promise<void>;
} & Pick<UsePopupTriggerHook, 'visibility$' | 'hide'>
export const EditUserPopup = ({visibility$, hide, selectedUserIdRef, onSuccess}: EditUserPopupProps) => {
    const showPopup = useObservable(visibility$, false);
    if (selectedUserIdRef.current === null) {
        return null;
    }
    return (
        <Dialog
            isShown={showPopup}
            hasHeader={false}
            hasFooter={false}
            width={400}
            containerProps={ {
                maxHeight: USER_POPUP_HEIGHT,
                marginY: `calc((100vh - ${ USER_POPUP_HEIGHT }px) / 2)`,
                overflowY: 'auto'
            } }
            onCloseComplete={hide}
        >
            {({close}) => {
                if (selectedUserIdRef.current === null) {
                    return null;
                }
                return (
                    <>
                        <Heading size={600} textAlign='center' marginY='16px'>Edit User</Heading>
                        <FullUserInfoFetcher oktaId={selectedUserIdRef.current} closePopupCallback={close} onSuccess={onSuccess}/>
                    </>
                );
            }}
        </Dialog>
    );
};

type FullUserInfoFetcherProps = {
    oktaId: string;
    closePopupCallback: () => void;
    onSuccess?: () => Promise<void>;
}
const FullUserInfoFetcher = ({oktaId, closePopupCallback, onSuccess}: FullUserInfoFetcherProps) => {
    const {data, loading, error} = useProviderUserByIdQuery(oktaId, true);

    if (error) {
        showToast.error(`Unable to retrieve full user info. Reason: ${error.message}`);
    }

    if (loading) {
        return (
            <>
                <Spinner marginX='auto'/>
                <Paragraph size={300} textAlign='center'>Fetching latest user info...</Paragraph>
            </>
        );
    }

    if (data?.providerUser) {
        return (<EditUserForm fullUserInfo={data.providerUser} closePopupCallback={closePopupCallback} onSuccess={onSuccess}/>)
    }
    return null;
};

type EditUserFormProps = {
    fullUserInfo: FullProviderUserInfo;
    closePopupCallback: () => void;
    onSuccess?: () => Promise<void>;
};
const EditUserForm = ({fullUserInfo, closePopupCallback, onSuccess}: EditUserFormProps) => {
    const currentUser = useCurrentUser();
    const apolloClient = useApolloClient();
    const successCallback = useCallback(() => {
        const isSelfEdit = !!currentUser?.username
            && currentUser.username.toLowerCase() === fullUserInfo.username?.toLowerCase();
        if (isSelfEdit) {
            // Granting/revoking your own access must re-sync the left navbar, which reads currentUser.
            apolloClient.refetchQueries({include: ['CurrentUser']});
        }
        closePopupCallback();
        if (onSuccess) {
            onSuccess();
        }
    }, [apolloClient, currentUser, fullUserInfo.username, closePopupCallback, onSuccess])
    const {methods, submit, isSubmitting$} = useValidatedEditUser(fullUserInfo, successCallback);
    return (
        <>
            <FormProvider {...methods}>
                <form className='flex flex-col items-center'>
                    <EditUserFormContent user={fullUserInfo}/>
                </form>
            </FormProvider>
            <Pane display='flex' justifyContent='center' paddingTop={12} paddingLeft={24} width='100%'>
                <CancelButton isSubmitting$={isSubmitting$} cancel={closePopupCallback}/>
                <OkButton text='Update' isSubmitting$={isSubmitting$} submit={submit} disabled={fullUserInfo.bhpnAdmin}/>
            </Pane>
        </>
    );
}
const useValidatedEditUser = (fullUserInfo: FullProviderUserInfo, successCallback: () => void) => {
    const {submit, isRunning$} = useEditProviderUserMutation$(successCallback);
    const isContactDisabled = useFeatureFlag(ProviderPortalFeatureFlag.BHPNPP_3240_IS_BIZ_CONTACT_DISABLED);
    const methods = useForm<UserFormInput, object>({
        defaultValues: {
            email: fullUserInfo.email,
            firstName: fullUserInfo.firstName,
            lastName: fullUserInfo.lastName,
            providerGuid: fullUserInfo.providerGuid,
            clientPlacementAccess: fullUserInfo.clientPlacementAccess,
            clientReportAccess: fullUserInfo.clientReportAccess,
            providerMetricsAccess: fullUserInfo.providerMetricsAccess,
            rosterAccess: fullUserInfo.rosterAccess,
            auditLogAccess: fullUserInfo.auditLogAccess,
            contactEditAccess: fullUserInfo.contactEditAccess,
            contactReadAccess: fullUserInfo.contactReadAccess,
            userManagementAdminAccess: fullUserInfo.userManagementAdminAccess,
            active: isUserActive(fullUserInfo.status),
        },
    });
    const {trigger, getValues} = methods;
    const updateUser = useCallback(async () => {
        const isValid = await trigger();
        if (isValid) {
            const validatedValues = getValues();
            await submit({
                oktaId: fullUserInfo.oktaId,
                payload: {
                    firstName: validatedValues.firstName ?? '',
                    lastName: validatedValues.lastName ?? '',
                    clientPlacementAccess: validatedValues.clientPlacementAccess ?? false,
                    clientReportAccess: validatedValues.clientReportAccess ?? false,
                    providerMetricsAccess: validatedValues.providerMetricsAccess ?? false,
                    rosterAccess: validatedValues.rosterAccess ?? false,
                    // A box hidden by a flag still posts. The form seeds it from the loaded user,
                    // so a grant made with the feature on survives an edit made with it off.
                    auditLogAccess: validatedValues.auditLogAccess ?? false,
                    contactEditAccess: validatedValues.contactEditAccess ?? false,
                    // Contact Management is the exception. While its box is hidden it posts true,
                    // so a user whose grants were cleared gets the page back on the next save.
                    contactReadAccess: isContactDisabled ? true : validatedValues.contactReadAccess ?? false,
                    userManagementAdminAccess: validatedValues.userManagementAdminAccess ?? false,
                    active: validatedValues.active ?? true,
                },
            });
        }
    }, [trigger, getValues, submit, fullUserInfo.oktaId, isContactDisabled]);

    return {
        methods,
        isSubmitting$: isRunning$,
        submit: updateUser,
    };
};
